import React from "react";
import { useMutation, useQuery } from "@apollo/client";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";

import { PageTemplate } from "@components/pageTemplate/PageTemplate";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { useCurrentUser } from "@modules/authentication/useCurrentUser";
import {
  getPasswordPolicyErrors,
  PASSWORD_POLICY_DESCRIPTION,
} from "@modules/authentication/passwordPolicy";
import {
  ChangeOwnPasswordDocument,
  ChangeOwnPasswordMutation,
  ChangeOwnPasswordMutationVariables,
  PasswordAuthStatusDocument,
  PasswordAuthStatusQuery,
  SetOwnPasswordDocument,
  SetOwnPasswordMutation,
  SetOwnPasswordMutationVariables,
} from "generated/graphql";

export const ProfileSettingsPage = () => {
  const { currentUser } = useCurrentUser();
  const [currentPassword, setCurrentPassword] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [changeOwnPassword, changeOwnPasswordMutation] = useMutation<
    ChangeOwnPasswordMutation,
    ChangeOwnPasswordMutationVariables
  >(ChangeOwnPasswordDocument);
  const [setOwnPassword, setOwnPasswordMutation] = useMutation<
    SetOwnPasswordMutation,
    SetOwnPasswordMutationVariables
  >(SetOwnPasswordDocument);
  const { data: passwordStatus, refetch: refetchPasswordStatus } =
    useQuery<PasswordAuthStatusQuery>(PasswordAuthStatusDocument, {
      fetchPolicy: "network-only",
    });

  const hasPassword = passwordStatus?.passwordAuthStatus.hasPassword ?? true;
  const passwordPolicyErrors = React.useMemo(
    () => getPasswordPolicyErrors(newPassword),
    [newPassword]
  );
  const canChangePassword =
    (!hasPassword || currentPassword.length > 0) &&
    passwordPolicyErrors.length === 0 &&
    !changeOwnPasswordMutation.loading &&
    !setOwnPasswordMutation.loading;

  const handleSubmitPassword = async () => {
    if (!canChangePassword) {
      return;
    }

    try {
      if (hasPassword) {
        const result = await changeOwnPassword({
          variables: {
            input: {
              currentPassword,
              password: newPassword,
            },
          },
        });

        if (!result.data?.changeOwnPassword) {
          toast.error(
            "Unable to change password. Check your current password and try again."
          );
          return;
        }
      } else {
        const result = await setOwnPassword({
          variables: {
            input: {
              password: newPassword,
            },
          },
        });

        if (!result.data?.setOwnPassword) {
          toast.error("Unable to set password");
          return;
        }
      }

      setCurrentPassword("");
      setNewPassword("");
      await refetchPasswordStatus();
      toast.success(hasPassword ? "Password updated" : "Password set");
    } catch (error) {
      toast.error(
        hasPassword
          ? "Unable to change password. Check your current password and try again."
          : `Unable to set password: ${
              error instanceof Error ? error.message : "Unknown error"
            }`
      );
    }
  };

  return (
    <PageTemplate
      title="Profile settings"
      subtitle={currentUser?.email ?? undefined}
      breadcrumbs={[
        { label: "Home", path: "/" },
        { label: "Profile settings", path: "/profile/settings" },
      ]}
      browserTitle="Profile settings"
      headerRule
    >
      <div className="max-w-2xl">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="size-5" />
              Password
            </CardTitle>
            <CardDescription>
              {hasPassword
                ? "Enter your current password and choose a new password."
                : "Choose a password so you can sign in without waiting for a magic link."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              className="grid gap-4"
              onSubmit={(event) => {
                event.preventDefault();
                void handleSubmitPassword();
              }}
            >
              {hasPassword && (
                <div className="grid gap-2">
                  <Label htmlFor="profile-current-password">
                    Current password
                  </Label>
                  <Input
                    id="profile-current-password"
                    type="password"
                    autoComplete="current-password"
                    value={currentPassword}
                    onChange={(event) => setCurrentPassword(event.target.value)}
                    disabled={
                      changeOwnPasswordMutation.loading ||
                      setOwnPasswordMutation.loading
                    }
                  />
                </div>
              )}
              <div className="grid gap-2">
                <Label htmlFor="profile-new-password">New password</Label>
                <Input
                  id="profile-new-password"
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  disabled={
                    changeOwnPasswordMutation.loading ||
                    setOwnPasswordMutation.loading
                  }
                />
                <p className="text-xs text-muted-foreground">
                  {PASSWORD_POLICY_DESCRIPTION}
                </p>
                {newPassword.length > 0 && passwordPolicyErrors.length > 0 && (
                  <ul className="list-disc space-y-1 pl-5 text-xs text-destructive">
                    {passwordPolicyErrors.map((error) => (
                      <li key={error}>{error}</li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="flex justify-end">
                <Button type="submit" disabled={!canChangePassword}>
                  {hasPassword ? "Change password" : "Set password"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </PageTemplate>
  );
};

export default ProfileSettingsPage;
